import type {
  DecorationCatalog,
  PrintCategory,
  PrintVariant,
} from "./design-catalog.types";

export function getPrintCategory(
  catalog: DecorationCatalog | undefined,
  categoryId: string,
): PrintCategory | undefined {
  return catalog?.printCategories.find((c) => c.id === categoryId);
}

export function getPrintVariant(
  catalog: DecorationCatalog | undefined,
  categoryId: string,
  variantId: string,
): PrintVariant | undefined {
  return getPrintCategory(catalog, categoryId)?.variants.find((v) => v.id === variantId);
}

export function defaultVariantId(
  catalog: DecorationCatalog | undefined,
  categoryId: string,
): string {
  return getPrintCategory(catalog, categoryId)?.variants[0]?.id ?? "";
}

export function defaultEstimatedSizeId(
  variant: PrintVariant | undefined,
): string {
  const sizeField = variant?.fields.find((f) => f.key === "estimatedSize");
  return sizeField?.options?.[0]?.id ?? "";
}

/** True when the variant binds a catalog field (e.g. Tags → Applique Woven Tags). */
export function variantHasField(
  catalog: DecorationCatalog | undefined,
  categoryId: string,
  variantId: string,
  fieldKey: string,
): boolean {
  return Boolean(
    getPrintVariant(catalog, categoryId, variantId)?.fields.some(
      (f) => f.key === fieldKey,
    ),
  );
}

export function getDecoratingMethod(
  catalog: DecorationCatalog | undefined,
  methodKey: string,
) {
  return catalog?.decoratingMethods.find((m) => m.id === methodKey);
}

export function getDecoratingMethodByLabel(
  catalog: DecorationCatalog | undefined,
  label: string,
) {
  return catalog?.decoratingMethods.find((m) => m.label === label);
}

/** Match catalog method id or legacy label stored on drafts. */
export function resolveDecoratingMethodId(
  catalog: DecorationCatalog | undefined,
  stored: string,
): string | undefined {
  const byId = getDecoratingMethod(catalog, stored);
  if (byId) return byId.id;
  return getDecoratingMethodByLabel(catalog, stored)?.id;
}

export function decoratingMethodLabel(
  catalog: DecorationCatalog | undefined,
  methodId: string,
): string {
  return getDecoratingMethod(catalog, methodId)?.label ?? methodId;
}
